import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { ClickNavigation, ClickNavigationSection } from '../../../../components/NavigationWithSideBar/ClickNavigation'
import { getIntegrationSettingsSchema, IntegrationSettings } from './schema'
import { FormSaveBar } from '../../../../components/FormSaveBar/FormSaveBar'
import { useUnsavedChangesGuard } from '../../../../common/useUnsavedChangesGuard'
import { useGetEnabledWorkflows, useGetIntegrationAdminSettings } from './hooks'
import { integrationHasAdminMappingSettings, IntegrationServiceId, isBusinessEventExportEnabledForService } from '../../../../common/integrations'
import { DebugZodErrors } from './ZodValidator'
import { defaultCRMAccountIntegrationConfig } from './AdminSettingsTabs/model'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { useSaveBusinessEventExportWorkflow } from '../../../Workflows/hooks'

interface IntegrationAdminSettingsContainerProps {
  sections: ClickNavigationSection[]
  serviceId: IntegrationServiceId
}

export const IntegrationAdminSettingsContainer: React.FC<IntegrationAdminSettingsContainerProps> = ({ sections, serviceId }) => {
  const { isAccountUpdating, saveAccountMappingConfig, currentIntegrationConfig, currentIntegrationFormValues } = useGetIntegrationAdminSettings(serviceId)
  const hasAdminMappingSettings = integrationHasAdminMappingSettings(serviceId)
  const { businessEventExportWorkflowContext, isWorkflowFetching } = useGetEnabledWorkflows()
  const { saveBusinessEventExportWorkflow } = useSaveBusinessEventExportWorkflow()
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const [formKey, setFormKey] = useState(0)

  const { getQuotaValue } = useZocksQuota()

  const additionalObjectsLimit = getQuotaValue('integration.additionalObjMax.value')
  const integrationSettingsSchema = getIntegrationSettingsSchema(additionalObjectsLimit as number, hasAdminMappingSettings)
  const methods = useForm<IntegrationSettings>({
    resolver: zodResolver(integrationSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      ...(currentIntegrationFormValues || defaultCRMAccountIntegrationConfig(serviceId)),
      enabledBusinessEventTypes: businessEventExportWorkflowContext?.businessEventTypes ?? [],
    },
    shouldUnregister: false,
  })

  const {
    reset,
    formState: { isDirty, isSubmitting, isValid, errors },
  } = methods

  useEffect(() => {
    if (currentIntegrationFormValues && !isWorkflowFetching && !isSubmitting) {
      reset({
        ...currentIntegrationFormValues,
        enabledBusinessEventTypes: businessEventExportWorkflowContext?.businessEventTypes ?? [],
      })
    }
  }, [currentIntegrationFormValues, businessEventExportWorkflowContext, isWorkflowFetching, isSubmitting, reset])

  const onSubmit = async (data: IntegrationSettings) => {
    await saveAccountMappingConfig(data)
    if (isBusinessEventExportEnabledForService(serviceId)) {
      await saveBusinessEventExportWorkflow({ businessEventTypes: data.enabledBusinessEventTypes ?? [] })
    }
    reset(data)
  }

  const handleDiscard = () => {
    reset()
    setFormKey((prevKey) => prevKey + 1)
  }

  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty, isSubmitting)

  const handleExportAdminSettings = () => {
    if (currentIntegrationConfig) {
      const blob = new Blob([JSON.stringify(currentIntegrationConfig)], { type: 'application/json' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${accountId}_${serviceId}_settings.json`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } else {
      dispatch(
        addToast({
          variant: 'info',
          title: 'Exporting to file',
          messages: ['Unable to export: no configuration has been saved. Please save your configuration first.'],
        }),
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <form id='integration-settings-form' onSubmit={methods.handleSubmit(onSubmit)} className='d-flex flex-column min-vh-100'>
        <div className='flex-grow-1 zocks-pb-10.0'>
          <ClickNavigation
            sections={sections}
            defaultActiveSectionId={sections[0].id}
            key={formKey}
            NavigationFooter={<AdminSettingsNavigationFooter handleExportAdminSettings={handleExportAdminSettings} hasUnsavedChanges={isDirty} />}
          />
        </div>
        <FormSaveBar
          formId='integration-settings-form'
          isVisible={isDirty}
          isSubmitting={isSubmitting || isAccountUpdating}
          isValid={isValid}
          isDirty={isDirty}
          errors={errors}
          onDiscard={handleDiscard}
          showNavigationWarning={isBlocked}
          onCancelLeave={cancelLeave}
          onConfirmLeave={confirmLeave}
        />
        <DebugZodErrors shouldDebug={false} />
      </form>
    </FormProvider>
  )
}
interface AdminSettingsNavigationFooterProps {
  handleExportAdminSettings: () => void
  hasUnsavedChanges: boolean
}

const UNSAVED_CHANGES_EXPORT_TOOLTIP = 'Save or discard your changes before exporting. The export only includes saved settings.'

const AdminSettingsNavigationFooter: React.FC<AdminSettingsNavigationFooterProps> = ({ handleExportAdminSettings, hasUnsavedChanges }) => {
  return (
    <div>
      <ScopedBlock
        missingScopeAction={MissingScopeAction.DISABLE}
        scope={{
          scope: 'account:read',
          superScope: ['account:admin'],
        }}
      >
        <Tooltip title={hasUnsavedChanges ? UNSAVED_CHANGES_EXPORT_TOOLTIP : undefined}>
          <span tabIndex={hasUnsavedChanges ? 0 : undefined}>
            <Button type='button' className='ms-1 button-link-small' onClick={handleExportAdminSettings} disabled={hasUnsavedChanges}>
              Export settings to file
            </Button>
          </span>
        </Tooltip>
      </ScopedBlock>
    </div>
  )
}
